import type {
  IAttributePropertyOption,
  IKeyPersonDetail,
} from '@commenda/types';
import { fetcher } from '@commenda/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ownershipKeys } from '../ownership/keys';
import { personsInEntityKeys } from '../persons/usePersonsInEntity';
import { keyPersonsKeys } from './keys';
import { KEY_PERSONS_ROUTE } from './routes';
import type {
  AssignRolesParams,
  AssignRolesResult,
  BulkRemoveRolesParams,
  BulkRemoveRolesResult,
  CreateAttributePropertyOptionParams,
  CreateIdentityProofParams,
  CreateKeyPersonParams,
  CreateKeyPersonResult,
  DeleteRoleAssignmentParams,
  DeleteRoleAssignmentResult,
  DuplicateKeyPersonMatch,
  EditRoleAssignmentDatesParams,
  EditRoleAssignmentDatesResult,
  EndUboRoleParams,
  EndUboRoleResult,
  UpdateIdentityProofParams,
  UpdateKeyPersonParams,
} from './types';

const DUPLICATE_KEY_PERSON_CODE = 'KEY_PERSON_ALREADY_EXISTS';

async function throwIfNotOk(res: Response, fallback: string) {
  if (res.ok) return;
  const body = (await res.json().catch(() => ({}))) as { message?: string };
  throw new Error(body.message || fallback);
}

export function useCreateKeyPerson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      params: CreateKeyPersonParams
    ): Promise<CreateKeyPersonResult> => {
      const res = await fetcher(KEY_PERSONS_ROUTE, {
        method: 'POST',
        body: params,
      });
      if (res.status === 409) {
        const body = (await res.json().catch(() => ({}))) as {
          code?: string;
          message?: string;
          matches?: DuplicateKeyPersonMatch[];
        };
        // Keyed on the payload rather than the code string alone: the people to choose between are what the
        // caller actually needs, and a 409 carrying them is the duplicate case whatever the code is named.
        if (
          Array.isArray(body.matches) ||
          body.code === DUPLICATE_KEY_PERSON_CODE
        ) {
          return { status: 'duplicate', matches: body.matches ?? [] };
        }
        throw new Error(body.message || 'Failed to create key person');
      }
      await throwIfNotOk(res, 'Failed to create key person');
      return {
        status: 'created',
        person: (await res.json()) as IKeyPersonDetail,
      };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keyPersonsKeys.lists() });
    },
  });
}

export function useCreateAttributePropertyOption() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: CreateAttributePropertyOptionParams) => {
      const res = await fetcher(
        `${KEY_PERSONS_ROUTE}/attribute-properties/options`,
        {
          method: 'POST',
          body: params,
        }
      );
      await throwIfNotOk(res, 'Failed to add option');
      return (await res.json()) as IAttributePropertyOption;
    },
    onSuccess: (_result, params) => {
      qc.invalidateQueries({
        queryKey: keyPersonsKeys.attributeProperties(params.companyId),
      });
    },
  });
}

export function useRemoveKeyPerson() {
  const qc = useQueryClient();
  return useMutation({
    // removalDate (ISO-8601) sets when roles + profile end; omitted → backend defaults to now.
    mutationFn: async ({
      personId,
      removalDate,
    }: {
      personId: number;
      removalDate?: string;
    }) => {
      const res = await fetcher(`${KEY_PERSONS_ROUTE}/${personId}`, {
        method: 'DELETE',
        body: { removalDate },
      });
      await throwIfNotOk(res, 'Failed to remove key person');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keyPersonsKeys.lists() });
    },
  });
}

export function useUpdateKeyPerson(id: number, companyId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: UpdateKeyPersonParams) => {
      const res = await fetcher(`${KEY_PERSONS_ROUTE}/${id}`, {
        method: 'PATCH',
        body: params,
      });
      await throwIfNotOk(res, 'Failed to update key person');
      return (await res.json()) as IKeyPersonDetail;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keyPersonsKeys.detail(id) });
      qc.invalidateQueries({ queryKey: keyPersonsKeys.lists() });
      // A save-for-future custom field on the profile registers a workspace property server-side.
      qc.invalidateQueries({
        queryKey: keyPersonsKeys.attributeProperties(companyId),
      });
    },
  });
}

export function useAssignRoles(id: number, companyId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: AssignRolesParams) => {
      const res = await fetcher(`${KEY_PERSONS_ROUTE}/${id}/role-assignments`, {
        method: 'POST',
        body: params,
      });
      await throwIfNotOk(res, 'Failed to assign roles');
      return (await res.json()) as AssignRolesResult;
    },
    onSuccess: (_result, params) => {
      qc.invalidateQueries({ queryKey: keyPersonsKeys.roleAssignments(id) });
      qc.invalidateQueries({
        queryKey: keyPersonsKeys.roleEligibleEntities(id),
      });
      // A save-for-future custom field on a role registers a workspace property server-side.
      qc.invalidateQueries({
        queryKey: keyPersonsKeys.attributeProperties(companyId),
      });
      // A role change flips who's active only at the entities it touched — refresh just those lists.
      const affectedCorporationIds = Array.from(
        new Set(params.items.map((item) => item.corporationId))
      );
      affectedCorporationIds.forEach((corporationId) =>
        qc.invalidateQueries({
          queryKey: keyPersonsKeys.forEntity(corporationId),
        })
      );
      // A role can carry ownership consequences, so the ownership tab is stale too.
      qc.invalidateQueries({ queryKey: ownershipKeys.all });
    },
  });
}

export function useBulkRemoveRoleAssignments(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: BulkRemoveRolesParams) => {
      const res = await fetcher(
        `${KEY_PERSONS_ROUTE}/${id}/role-assignments/bulk-remove`,
        {
          method: 'POST',
          body: params,
        }
      );
      await throwIfNotOk(res, 'Failed to end roles');
      return (await res.json()) as BulkRemoveRolesResult;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keyPersonsKeys.roleAssignments(id) });
      qc.invalidateQueries({
        queryKey: keyPersonsKeys.roleEligibleEntities(id),
      });
      // A role change flips who's active at an entity → refresh every per-entity list.
      qc.invalidateQueries({ queryKey: keyPersonsKeys.forEntities() });
      qc.invalidateQueries({ queryKey: ownershipKeys.all });
      qc.invalidateQueries({
        queryKey: [personsInEntityKeys.FETCH_PERSONS_IN_ENTITY],
      });
    },
  });
}

export function useDeleteRoleAssignment(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      assignmentId,
      reason,
    }: DeleteRoleAssignmentParams) => {
      const res = await fetcher(
        `${KEY_PERSONS_ROUTE}/${id}/role-assignments/${assignmentId}`,
        { method: 'DELETE', body: { reason } }
      );
      await throwIfNotOk(res, 'Failed to delete role');
      return (await res.json()) as DeleteRoleAssignmentResult;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keyPersonsKeys.roleAssignments(id) });
      qc.invalidateQueries({
        queryKey: keyPersonsKeys.roleEligibleEntities(id),
      });
      qc.invalidateQueries({ queryKey: keyPersonsKeys.forEntities() });
      qc.invalidateQueries({ queryKey: ownershipKeys.all });
      qc.invalidateQueries({
        queryKey: [personsInEntityKeys.FETCH_PERSONS_IN_ENTITY],
      });
    },
  });
}

export function useEditRoleAssignmentDates(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      assignmentId,
      ...dates
    }: EditRoleAssignmentDatesParams) => {
      const res = await fetcher(
        `${KEY_PERSONS_ROUTE}/${id}/role-assignments/${assignmentId}`,
        { method: 'PATCH', body: dates }
      );
      await throwIfNotOk(res, 'Failed to update role dates');
      return (await res.json()) as EditRoleAssignmentDatesResult;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keyPersonsKeys.roleAssignments(id) });
      qc.invalidateQueries({
        queryKey: keyPersonsKeys.roleEligibleEntities(id),
      });
      // Moving an end date can flip whether the role is active at its entity.
      qc.invalidateQueries({ queryKey: keyPersonsKeys.forEntities() });
      qc.invalidateQueries({ queryKey: ownershipKeys.all });
      qc.invalidateQueries({
        queryKey: [personsInEntityKeys.FETCH_PERSONS_IN_ENTITY],
      });
    },
  });
}

export function useEndUboRole(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: EndUboRoleParams) => {
      const res = await fetcher(
        `${KEY_PERSONS_ROUTE}/${id}/role-assignments/end-ubo`,
        {
          method: 'POST',
          body: params,
        }
      );
      await throwIfNotOk(res, 'Failed to remove UBO');
      return (await res.json()) as EndUboRoleResult;
    },
    onSuccess: (_result, { corporationId }) => {
      qc.invalidateQueries({ queryKey: keyPersonsKeys.roleAssignments(id) });
      qc.invalidateQueries({
        queryKey: keyPersonsKeys.roleEligibleEntities(id),
      });
      qc.invalidateQueries({ queryKey: keyPersonsKeys.forEntities() });
      qc.invalidateQueries({ queryKey: ownershipKeys.all });
      qc.invalidateQueries({
        queryKey: [
          personsInEntityKeys.FETCH_PERSONS_IN_ENTITY,
          { businessEntityId: corporationId },
        ],
      });
    },
  });
}

export function useCreateIdentityProof(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: CreateIdentityProofParams) => {
      const res = await fetcher(`${KEY_PERSONS_ROUTE}/${id}/identity-proofs`, {
        method: 'POST',
        body: params,
      });
      await throwIfNotOk(res, 'Failed to add proof of identity');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keyPersonsKeys.detail(id) });
    },
  });
}

export function useUpdateIdentityProof(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      assignmentId,
      ...params
    }: UpdateIdentityProofParams & { assignmentId: string }) => {
      const res = await fetcher(
        `${KEY_PERSONS_ROUTE}/${id}/identity-proofs/${assignmentId}`,
        { method: 'PATCH', body: params }
      );
      await throwIfNotOk(res, 'Failed to update proof of identity');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keyPersonsKeys.detail(id) });
    },
  });
}

export function useDeleteIdentityProof(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (assignmentId: string) => {
      const res = await fetcher(
        `${KEY_PERSONS_ROUTE}/${id}/identity-proofs/${assignmentId}`,
        { method: 'DELETE' }
      );
      await throwIfNotOk(res, 'Failed to remove proof of identity');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keyPersonsKeys.detail(id) });
    },
  });
}

// Dismisses an empty nationality-suggested slot (no assignmentId) so it stops appearing; reversible by re-adding.
export function useDismissIdentityProofSlot(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { country: string; kycId: string }) => {
      const res = await fetcher(
        `${KEY_PERSONS_ROUTE}/${id}/identity-proofs/dismiss-slot`,
        { method: 'POST', body: params }
      );
      await throwIfNotOk(res, 'Failed to remove proof of identity');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keyPersonsKeys.detail(id) });
    },
  });
}
